import { formatMoney } from '@shopify/theme-currency';

/*
 * Dynamic product block for blog articles.
 *
 * Usage in the article body text: place a marker on its own line, e.g.
 *   [product:mijn-product-handle]
 * or for a row of 2-3 products side by side, comma-separate the handles:
 *   [product:handle-1,handle-2,handle-3]
 *
 * This script finds the marker, fetches the live product(s) via the Shopify
 * AJAX endpoint (/products/{handle}.js) and replaces the marker with (a)
 * product card(s) that mirror the site's existing collection product cards.
 *
 * Depends on two globals rendered by main-article.liquid / html-bottom-js.liquid:
 *   window.moneyFormat                 -> shop.money_format
 *   window.dynamicProductBlockStrings  -> translated UI labels
 */
(function () {
  'use strict';

  var MARKER_REGEX = /\[product:([a-zA-Z0-9_,-]+)\]/;
  var MARKER_REGEX_GLOBAL = /\[product:([a-zA-Z0-9_,-]+)\]/g;
  var CONTENT_SELECTOR = '.article-template__content';
  var MAX_PRODUCTS_PER_MARKER = 3;

  var strings = window.dynamicProductBlockStrings || {};

  function init() {
    var root = document.querySelector(CONTENT_SELECTOR);
    if (!root) return;

    var textNodes = [];
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    var node;
    while ((node = walker.nextNode())) {
      if (MARKER_REGEX.test(node.nodeValue)) {
        textNodes.push(node);
      }
    }

    textNodes.forEach(function (textNode) {
      processTextNode(textNode, root);
    });
  }

  function parseHandles(raw) {
    var handles = raw
      .split(',')
      .map(function (h) { return h.trim(); })
      .filter(Boolean);

    if (handles.length > MAX_PRODUCTS_PER_MARKER) {
      if (window.console && window.console.warn) {
        console.warn(
          '[dynamic-product-block] Marker contains ' + handles.length + ' products, maximum is ' +
          MAX_PRODUCTS_PER_MARKER + '. Ignored: ' + handles.slice(MAX_PRODUCTS_PER_MARKER).join(', ')
        );
      }
      handles = handles.slice(0, MAX_PRODUCTS_PER_MARKER);
    }

    return handles;
  }

  function processTextNode(textNode, root) {
    var text = textNode.nodeValue;
    var trimmed = text.trim();
    var exactMatch = trimmed.match(/^\[product:([a-zA-Z0-9_,-]+)\]$/);

    if (exactMatch) {
      // The marker is the sole content of this text node (its own line/paragraph):
      // replace the whole containing block element with the product card row.
      var handles = parseHandles(exactMatch[1]);
      var blockEl = findBlockAncestor(textNode, root);
      var placeholder = createPlaceholder(handles);

      if (blockEl && blockEl.parentNode) {
        blockEl.parentNode.replaceChild(placeholder, blockEl);
      } else if (textNode.parentNode) {
        textNode.parentNode.replaceChild(placeholder, textNode);
      }

      loadProducts(handles, placeholder);
      return;
    }

    // Marker is embedded inside a sentence: remove the marker text and insert
    // the product card row as its own block right after the paragraph it was in.
    var inlineMatch = MARKER_REGEX.exec(text);
    if (!inlineMatch) return;

    var inlineHandles = parseHandles(inlineMatch[1]);
    textNode.nodeValue = text.replace(MARKER_REGEX_GLOBAL, '').replace(/[ \t]{2,}/g, ' ');

    var container = findBlockAncestor(textNode, root);
    var inlinePlaceholder = createPlaceholder(inlineHandles);

    if (container && container.parentNode) {
      container.parentNode.insertBefore(inlinePlaceholder, container.nextSibling);
    } else if (root) {
      root.appendChild(inlinePlaceholder);
    }

    loadProducts(inlineHandles, inlinePlaceholder);
  }

  function findBlockAncestor(node, root) {
    var el = node.parentElement;
    while (el && el !== root) {
      if (/^(P|DIV|LI|BLOCKQUOTE|H2|H3|H4)$/i.test(el.tagName)) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  function createPlaceholder(handles) {
    var el = document.createElement('div');
    el.className = 'dynamic-product-block-row dynamic-product-block-row--loading';
    el.setAttribute('data-product-handles', handles.join(','));
    return el;
  }

  function loadProducts(handles, placeholder) {
    if (!placeholder || !handles.length) {
      if (placeholder && placeholder.parentNode) {
        placeholder.parentNode.removeChild(placeholder);
      }
      return;
    }

    var requests = handles.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Product not found: ' + handle);
          }
          return response.json();
        })
        .catch(function (error) {
          if (window.console && window.console.warn) {
            console.warn('[dynamic-product-block] Could not load product "' + handle + '":', error);
          }
          return null;
        });
    });

    Promise.all(requests).then(function (products) {
      renderRow(products.filter(Boolean), placeholder);
    });
  }

  function money(cents) {
    if (typeof cents !== 'number') return '';
    return formatMoney(cents, window.moneyFormat);
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function withImageWidth(url, width) {
    if (!url) return url;
    var queryIndex = url.indexOf('?');
    var base = queryIndex === -1 ? url : url.slice(0, queryIndex);
    var query = queryIndex === -1 ? '' : url.slice(queryIndex);
    return base.replace(/(\.[a-zA-Z]{3,4})$/, '_' + width + 'x$1') + query;
  }

  function buildCardHtml(product) {
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var imageUrl = image ? escapeHtml(withImageWidth(image, 600)) : '';
    var productUrl = escapeHtml(product.url);
    var onSale = !!(product.compare_at_price && product.compare_at_price > product.price);
    var available = !!product.available;

    var priceHtml = onSale
      ? '<s class="price-item price-item--regular">' + escapeHtml(money(product.compare_at_price)) + '</s> ' +
        '<span class="price-item price-item--sale">' + escapeHtml(money(product.price)) + '</span>'
      : '<span class="price-item price-item--regular">' + escapeHtml(money(product.price)) + '</span>';

    var ctaLabel = available
      ? strings.viewProduct
      : (strings.soldOut ? strings.soldOut + ' – ' + strings.viewProduct : strings.viewProduct);

    return (
      '<div class="product-card-wrapper dynamic-product-block' + (available ? '' : ' dynamic-product-block--soldout') + '" data-product-handle="' + escapeHtml(product.handle) + '">' +
        '<div class="product-card product-card--product">' +
          '<div class="product-card__inner">' +
            '<div class="product-card__image">' +
              '<a href="' + productUrl + '" class="full-unstyled-link">' +
                (imageUrl ? '<img class="img--cover product-card__image--primary" src="' + imageUrl + '" alt="' + escapeHtml(product.title) + '" loading="lazy">' : '') +
              '</a>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="product-card__information">' +
          (product.vendor ? '<div class="product-card__vendor label-small weight-medium">' + escapeHtml(product.vendor) + '</div>' : '') +
          '<a href="' + productUrl + '" class="full-unstyled-link">' +
            '<span class="product-card__information-text label-medium weight-medium">' + escapeHtml(product.title) + '</span>' +
          '</a>' +
        '</div>' +
        '<div class="product-card__price is-flex is-aligned-center">' +
          '<div class="price weight-medium' + (available ? '' : ' price--sold-out') + (onSale ? ' price--on-sale' : '') + '">' +
            '<dl><div class="' + (onSale ? 'price__sale' : 'price__regular') + '"><dd>' + priceHtml + '</dd></div></dl>' +
          '</div>' +
        '</div>' +
        '<a href="' + productUrl + '" class="button button--sm dynamic-product-block__cta">' +
          escapeHtml(ctaLabel) +
        '</a>' +
      '</div>'
    );
  }

  function renderRow(products, placeholder) {
    if (!products.length) {
      if (placeholder.parentNode) {
        placeholder.parentNode.removeChild(placeholder);
      }
      return;
    }

    var row = document.createElement('div');
    row.className = 'dynamic-product-block-row dynamic-product-block-row--count-' + products.length;
    row.innerHTML = products.map(buildCardHtml).join('');

    if (placeholder.parentNode) {
      placeholder.parentNode.replaceChild(row, placeholder);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
